/**
 * Entity Slice
 * Manages entity type, business details, partners, UBO, authorized signatory
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  EntityType,
  Partner,
  BusinessDetails,
  UBOEntry,
} from '@/types/onboarding';
import { createEmptyPartner, INITIAL_VERIFICATION_STATUS } from '@/types/onboarding';

export interface EntityState {
  entityType: EntityType | null;
  businessDetails: BusinessDetails;
  partners: Partner[];
  authorizedSignatoryId: string | null;
  uboDeclaration: {
    declared: boolean;
    ubos: UBOEntry[];
  };
}

const emptyAddress = { line1: '', line2: '', city: '', state: '', pinCode: '' };

const initialState: EntityState = {
  entityType: null,
  businessDetails: {
    tradeName: '',
    cin: '',
    llpin: '',
    udyamNumber: '',
    shopsEstNumber: '',
    dateOfIncorporation: '',
    registrationNumber: '',
    registeredAddress: { ...emptyAddress },
    operatingAddress: { ...emptyAddress },
  },
  partners: [],
  authorizedSignatoryId: null,
  uboDeclaration: {
    declared: false,
    ubos: [],
  },
};

const entitySlice = createSlice({
  name: 'entity',
  initialState,
  reducers: {
    setEntityType: (state, action: PayloadAction<EntityType>) => {
      state.entityType = action.payload;
      // Reset entity-specific data when type changes
      state.businessDetails = { ...initialState.businessDetails };
      state.partners = [];
      state.authorizedSignatoryId = null;
      state.uboDeclaration = { declared: false, ubos: [] };
    },

    // ── Business Details ─────────────────────────────────
    updateBusinessDetails: (state, action: PayloadAction<Partial<BusinessDetails>>) => {
      state.businessDetails = { ...state.businessDetails, ...action.payload };
    },

    // ── Partners ─────────────────────────────────────────
    addPartner: (state) => {
      const id = `partner-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      state.partners.push(createEmptyPartner(id));
    },

    removePartner: (state, action: PayloadAction<string>) => {
      state.partners = state.partners.filter((p) => p.id !== action.payload);
      if (state.authorizedSignatoryId === action.payload) {
        state.authorizedSignatoryId = null;
      }
    },

    updatePartner: (state, action: PayloadAction<{ id: string; data: Partial<Partner> }>) => {
      const idx = state.partners.findIndex((p) => p.id === action.payload.id);
      if (idx !== -1) {
        state.partners[idx] = { ...state.partners[idx], ...action.payload.data };
      }
    },

    setPartnerVerification: (
      state,
      action: PayloadAction<{
        partnerId: string;
        type: 'aadhaar' | 'pan' | 'din';
        status: typeof INITIAL_VERIFICATION_STATUS;
      }>
    ) => {
      const idx = state.partners.findIndex((p) => p.id === action.payload.partnerId);
      if (idx !== -1) {
        state.partners[idx].verification[action.payload.type] = action.payload.status;
      }
    },

    // ── Authorized Signatory ─────────────────────────────
    setAuthorizedSignatory: (state, action: PayloadAction<string>) => {
      // Clear old
      state.partners.forEach((p) => (p.isAuthorizedSignatory = false));
      const idx = state.partners.findIndex((p) => p.id === action.payload);
      if (idx !== -1) {
        state.partners[idx].isAuthorizedSignatory = true;
      }
      state.authorizedSignatoryId = action.payload;
    },

    // ── UBO ──────────────────────────────────────────────
    setUBODeclared: (state, action: PayloadAction<boolean>) => {
      state.uboDeclaration.declared = action.payload;
    },

    setUBOs: (state, action: PayloadAction<UBOEntry[]>) => {
      state.uboDeclaration.ubos = action.payload;
      state.uboDeclaration.declared = true;
    },

    addUBO: (state, action: PayloadAction<UBOEntry>) => {
      state.uboDeclaration.ubos.push(action.payload);
    },

    removeUBO: (state, action: PayloadAction<number>) => {
      state.uboDeclaration.ubos.splice(action.payload, 1);
    },

    // ── Reset ────────────────────────────────────────────
    resetEntity: () => initialState,

    // ── Hydrate from Firestore on login/session restore ──
    hydrateFromFirestore: (
      state,
      action: PayloadAction<{
        entityType?: EntityType;
        businessDetails?: Partial<BusinessDetails>;
        partners?: Partner[];
      }>
    ) => {
      const { entityType, businessDetails, partners } = action.payload;
      if (entityType) state.entityType = entityType;
      if (businessDetails && Object.keys(businessDetails).length > 0) {
        state.businessDetails = { ...state.businessDetails, ...businessDetails };
      }
      if (partners && partners.length > 0) state.partners = partners;
    },
  },
});

export const {
  setEntityType,
  updateBusinessDetails,
  addPartner,
  removePartner,
  updatePartner,
  setPartnerVerification,
  setAuthorizedSignatory,
  setUBODeclared,
  setUBOs,
  addUBO,
  removeUBO,
  resetEntity,
  hydrateFromFirestore,
} = entitySlice.actions;

export default entitySlice.reducer;
